/**
 * @fileoverview The three ways a picture gets into Grow: the camera, a file, Google Photos.
 *
 * The same choice is offered when uploading a new photo and when replacing an
 * existing one, so it is the same component. The file input's id is generated
 * rather than fixed: a card mounts two of these dialogs at once, and a
 * duplicated id makes the label open the wrong one.
 *
 * Three rows, each saying what it does, the same rows as Edit plant's. They
 * were three buttons with a sentence under them repeating their names.
 *
 * Given onFilesChosen, it takes several at once, as the Photos page does: the
 * device's chooser and Google Photos both let more than one be picked.
 *
 * Google Photos opens in one tap, in a window of its own. While the gardener
 * chooses there, its row says so ("Choose your photos in Google Photos, then
 * come back") and stays tappable, so a window that did not appear can be
 * opened again; a Cancel under it stops the wait. While the photos come over,
 * the row counts them.
 *
 * Where: UploadPhotoDialog, EditPhotoDialog's Replace, and the photo tray's
 * TrayUploader (with onFilesChosen, several at once).
 * API: none of its own; Google Photos through useGooglePhotos.
 * Settings: VITE_GOOGLE_CLIENT_ID (without it the Google row says it is
 * unavailable).
 * Words (en/es): plants.photo.source.* (camera, cameraDetail, device,
 * deviceDetail, google, googleDetail, googleOpening, googleBringing,
 * googleBringingOne), plants.photo.take, sitewide.cancel.
 *
 * @module PhotoSourcePicker
 */

import React from 'react';
import { Camera, ChevronRight, Image, ImagePlus, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { GooglePhotoPick } from '@/shared/services/GooglePhotosService';
import type { GooglePhotosStatus } from './useGooglePhotos';
import type { Camera as CameraControls } from './useCamera';

/** One picture chosen, and the day it was taken where the source says. */
export interface ChosenPicture {
  /** The picture as the source handed it over. */
  file: File;
  /** The day the source says it was taken; absent where the file's EXIF has to say. */
  takenAt?: Date | null;
  /** Google Photos' id for the item, when it was picked there (B61: the same item twice is skipped). */
  googleItemId?: string;
}

/** What the source rows need: the camera, and where each choice goes. */
interface PhotoSourcePickerProps {
  /** The dialog's camera (useCamera). */
  camera: CameraControls;
  /** Called with whatever the camera captured. */
  onCaptured: (file: File) => void;
  /**
   * Called with the file chosen from the device, and — where the source knows
   * it — the day the picture was taken. A file from disk keeps its EXIF, so
   * nothing needs to be passed; Google's bytes are a resize with the EXIF
   * stripped out, so for those the date has to travel separately.
   */
  onFileChosen: (file: File, takenAt?: Date | null, googleItemId?: string) => void;
  /**
   * Given, several pictures can be chosen at once from the device or Google
   * Photos, and they all come here instead of to onFileChosen.
   */
  onFilesChosen?: (pictures: ChosenPicture[]) => void;
  /**
   * Opens Google Photos; resolves with the chosen pictures, or none.
   *
   * @param many - Whether to bring back every picture picked.
   */
  onPickGoogle: (many?: boolean) => Promise<GooglePhotoPick[]>;
  /** True from the tap on Google Photos until its photos are in. */
  isPickingGoogle: boolean;
  /** Where a Google Photos pick has got to; idle when left out. */
  googleStatus?: GooglePhotosStatus;
  /** How many photos are coming over from Google, while fetching. */
  googleCount?: number;
  /** Stops a Google Photos pick under way. */
  onCancelGoogle?: () => void;
  /** What went wrong with Google Photos last time, if anything. */
  googleError: string | null;
}

/** The look of one source row, shared by the two buttons and the label between them. */
const ROW = 'flex min-h-[3.75rem] w-full cursor-pointer items-center gap-3 rounded-md border bg-background px-3 py-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50';

/**
 * A row's icon, its name and what it does, and the chevron. `wrap` lets the
 * second line run to two, for a sentence that matters more than the row's height.
 *
 * @param props.icon - The row's icon.
 * @param props.title - What it is.
 * @param props.detail - What it does, or where a Google pick has got to.
 * @param props.wrap - Let the second line wrap.
 */
const SourceRow: React.FC<{ icon: React.ReactNode; title: string; detail: string; wrap?: boolean }> = ({ icon, title, detail, wrap }) => (
  <>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">{icon}</span>
    <span className="min-w-0 grow">
      <span className="block truncate text-[0.9375rem] font-medium">{title}</span>
      <span className={`block text-xs text-muted-foreground ${wrap ? '' : 'truncate'}`}>{detail}</span>
    </span>
    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  </>
);

/**
 * The camera, the device and Google Photos, as three rows; the camera's view
 * while it is on. Why the camera or Google Photos last failed is said under
 * the rows.
 *
 * @param props - PhotoSourcePickerProps.
 */
export const PhotoSourcePicker: React.FC<PhotoSourcePickerProps> = ({
  camera, onCaptured, onFileChosen, onFilesChosen, onPickGoogle, isPickingGoogle,
  googleStatus = 'idle', googleCount = 0, onCancelGoogle, googleError,
}) => {
  const { reactTranslate: t } = useTranslation();
  const fileInputId = React.useId();
  const many = Boolean(onFilesChosen);
  /*
   * A picture being taken. The camera stays live until the frame is encoded,
   * so a second tap in that moment took a second picture, and on Add photos
   * uploaded the same photo twice. The ref answers at once; the state greys
   * the button.
   */
  const capturing = React.useRef(false);
  const [isCapturing, setIsCapturing] = React.useState(false);

  /** Takes one picture, ignoring taps while one is being taken. */
  const capture = async (): Promise<void> => {
    if (capturing.current) return;
    capturing.current = true;
    setIsCapturing(true);
    try {
      const file = await camera.capture();
      if (file) onCaptured(file);
    } finally {
      capturing.current = false;
      setIsCapturing(false);
    }
  };
  /** The Google Photos row's second line, by where the pick has got to. */
  const googleDetail = {
    idle: t('plants.photo.source.googleDetail'),
    opening: t('plants.photo.source.googleOpening'),
    waiting: t(many ? 'plants.photo.source.googleWaitingMany' : 'plants.photo.source.googleWaitingOne'),
    fetching: googleCount === 1
      ? t('plants.photo.source.googleBringingOne')
      : t('plants.photo.source.googleBringing', { count: googleCount }),
  }[googleStatus];

  // The camera takes the picture's place, filling the room, with its two
  // buttons under it.
  if (camera.isActive) {
    return (
      <>
        <div className="relative min-h-[7.5rem] flex-1 overflow-hidden rounded-md bg-black">
          <video
            ref={camera.videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 h-full w-full object-contain"
          />
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            className="h-11 flex-1"
            disabled={isCapturing}
            onClick={() => { void capture(); }}
          >
            <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('plants.photo.take')}
          </Button>
          <Button variant="outline" className="h-11" onClick={camera.stop}>
            <X className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('sitewide.cancel')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="shrink-0 space-y-2.5">
      <button type="button" onClick={camera.start} className={ROW}>
        <SourceRow
          icon={<Camera className="h-5 w-5" aria-hidden="true" />}
          title={t('plants.photo.source.camera')}
          detail={t('plants.photo.source.cameraDetail')}
        />
      </button>

      {/* A label, so the tap reaches the hidden file input on every browser;
          focusable and answering Enter and Space, so a keyboard reaches it too. */}
      <label
        htmlFor={fileInputId}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          document.getElementById(fileInputId)?.click();
        }}
        className={ROW}
      >
        <SourceRow
          icon={<Image className="h-5 w-5" aria-hidden="true" />}
          title={t('plants.photo.source.device')}
          detail={t('plants.photo.source.deviceDetail')}
        />
      </label>
      <input
        id={fileInputId}
        type="file"
        accept="image/*"
        multiple={many}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          // Cleared, so choosing the same file again still counts as a choice.
          event.target.value = '';
          if (files.length === 0) return;
          if (onFilesChosen) onFilesChosen(files.map((file) => ({ file })));
          else onFileChosen(files[0]);
        }}
      />

      {/* onPickGoogle is called first thing in the tap, nothing awaited
          before it, so the browser lets Google Photos' window open. Tappable
          while waiting, so a window that did not appear can be opened again. */}
      <button
        type="button"
        onClick={async () => {
          const picked = await onPickGoogle(many);
          if (picked.length === 0) return;
          if (onFilesChosen) onFilesChosen(picked);
          else onFileChosen(picked[0].file, picked[0].takenAt, picked[0].googleItemId);
        }}
        disabled={googleStatus === 'opening' || googleStatus === 'fetching'}
        aria-busy={isPickingGoogle}
        className={ROW}
      >
        <SourceRow
          icon={isPickingGoogle ? <Spinner size={18} color="hsl(var(--primary))" /> : <ImagePlus className="h-5 w-5" aria-hidden="true" />}
          title={t('plants.photo.source.google')}
          detail={googleDetail}
          wrap={googleStatus !== 'idle'}
        />
      </button>
      {isPickingGoogle && onCancelGoogle && (
        <div className="flex justify-end">
          <Button type="button" variant="ghost" className="h-11" onClick={onCancelGoogle}>
            {t('sitewide.cancel')}
          </Button>
        </div>
      )}

      {/* Whatever went wrong, said here. Clicking the button and getting
          silence was the worst version of every failure this can have. */}
      {camera.error && (
        <p role="alert" className="text-sm text-destructive">{camera.error}</p>
      )}
      {googleError && (
        <p role="alert" className="text-sm text-destructive">{googleError}</p>
      )}
    </div>
  );
};
